// @flow
import * as React from 'react';

type OnChange = boolean => mixed;

/** Hook wrapper around the intersection observer API
 * * @type T The HTMLElement type to attach to
 * @param onChange A callback. If provided, creates an intersection observer and registers the callback as an event
 * @param rootMargin Grows/shrinks each side of the element's bounding box before computing intersections. If a number is provided, assume the root margin is in pixels
 * @param threshold A number to specify a pixel-count fuzz-factor for determining visibility
 * @returns {{current: (T|null)}} A ref to attach to an HTMLElement
 */
const useVisibleEffect = <T>(
  onChange: OnChange | null,
  rootMargin: number | string = 0,
  threshold: number = 0,
): { current: T | null } => {
  const elementRef = React.useRef<T | null>(null);
  const observedRef = React.useRef(null);
  const visibleRef = React.useRef(null);
  const intersectionObserverRef = React.useRef(null);
  const onIntersectingEntries = entries => {
    const wasVisible = visibleRef.current;
    const isVisible = entries.reduce((viewed, entry) => viewed || entry.isIntersecting, false);
    // If visibility is being initially set, OR if we transition from visible -> not visible OR if we transition from not visible -> visible
    const didChange =
      wasVisible === null || (!wasVisible && isVisible) || (wasVisible && !isVisible);
    if (onChange && didChange) {
      onChange(isVisible);
    }
    visibleRef.current = isVisible;
  };

  // Prevents a stale closure for the entire life cycle of the intersection observer for watching entries since the entries callback needs to be stable.
  // Makes the callback stable by delegating it to another callback that's updated appropriately through effects
  const onIntersectingEntriesProxyRef = React.useRef(onIntersectingEntries);

  React.useEffect(() => {
    onIntersectingEntriesProxyRef.current = onIntersectingEntries;
  });

  const onIntersectingEntriesCallback = React.useCallback(entries => {
    onIntersectingEntriesProxyRef.current(entries);
  }, []);

  // If no onChange function was passed in, then no point in creating an intersection observer
  const shouldObserve = !!onChange;

  // This effect is used to instantiate and eventually clean up the intersection observer
  React.useEffect(() => {
    if (shouldObserve && typeof IntersectionObserver !== 'undefined') {
      intersectionObserverRef.current = new IntersectionObserver(onIntersectingEntriesCallback, {
        rootMargin: typeof rootMargin === 'number' ? `${rootMargin}px` : rootMargin,
        threshold,
      });
    }
    return () => {
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
      }
      observedRef.current = null;
    };
  }, [shouldObserve, onIntersectingEntriesCallback, rootMargin, threshold]);

  // This effect is used to update the intersection observer when the ref points to a new DOM element. It should happen after every render
  // Needed since effects depending on variables in render happen too early for refs
  React.useEffect(() => {
    // If the current element changes, begin observing the new element and unobserve the old one
    if (
      intersectionObserverRef.current !== null &&
      elementRef.current &&
      (observedRef.current === null || elementRef.current !== observedRef.current)
    ) {
      const intersectionObserver = intersectionObserverRef.current;
      // See comment above
      intersectionObserver.observe((elementRef.current: any));
      if (observedRef.current) {
        intersectionObserver.unobserve((observedRef.current: any));
      }
      observedRef.current = elementRef.current;
    }
  });
  return elementRef;
};

export default useVisibleEffect;
